import { useEffect, useState } from 'react';

import { isMotionReduced } from '#src/app/shared/utils/accessibility';

/** The prototype's cadence. Only honoured while the whole run stays under {@link MAX_DURATION_MS}. */
const STEP_MS = 73;
/**
 * At 73ms a step, a petition with a few hundred senders would count for half a minute. The cadence is
 * a flourish, not information, so it compresses rather than outstaying its welcome.
 */
const MAX_DURATION_MS = 1500;

type State = { total: number | undefined; value: number | undefined };

function startingValue(total: number | undefined): number | undefined {
	if (total === undefined) return undefined;
	// `isMotionReduced()` reports true on the server, so SSR renders the final value and hydration
	// never flashes a 1.
	if (isMotionReduced() || total <= 1) return total;
	return 1;
}

/**
 * Counts 1 → `total` for the success screen's supporter count.
 *
 * Resolves straight to `total` when the viewer prefers reduced motion, when the count is still
 * loading, or when there is nothing to count up to.
 *
 * Driven by elapsed time rather than a per-step interval. A step-per-tick interval cannot hold the
 * duration cap — past ~1500 supporters its delay floors at 1ms, the browser clamps it to ~4ms, and
 * the run stretches to tens of seconds — and it goes on ticking after the total is reached, which
 * re-rendered this screen and the share sheet above it for as long as either stayed mounted.
 */
export function useCountUp(total: number | undefined): number | undefined {
	const [state, setState] = useState<State>(() => ({ total, value: startingValue(total) }));

	// Reset during render rather than in an effect: the count arrives asynchronously, and an effect
	// would paint one frame of the previous total before correcting itself.
	if (state.total !== total) {
		setState({ total, value: startingValue(total) });
	}

	useEffect(() => {
		if (total === undefined || isMotionReduced() || total <= 1) return undefined;

		const durationMs = Math.min(MAX_DURATION_MS, (total - 1) * STEP_MS);
		const startedAt = performance.now();
		let frame: number;

		const tick = (): void => {
			const progress = Math.min(1, (performance.now() - startedAt) / durationMs);
			const value = 1 + Math.round((total - 1) * progress);

			// Same value, same state object: React bails out, so the frames between two integers cost
			// nothing. Reaching the total schedules no further frame.
			setState((current) => (current.value === value ? current : { total, value }));

			if (progress < 1) frame = requestAnimationFrame(tick);
		};

		frame = requestAnimationFrame(tick);

		return () => cancelAnimationFrame(frame);
	}, [total]);

	return state.value;
}
